import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, RotateCw, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  apiKeysListOptions,
  useCreateApiKey,
  useRotateApiKey,
  useRevokeApiKey,
} from "@/features/api-keys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";

export function ApiKeysPage() {
  const { data: keys, isLoading } = useQuery(apiKeysListOptions());
  const createKey = useCreateApiKey();
  const rotateKey = useRotateApiKey();
  const revokeKey = useRevokeApiKey();

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyRole, setNewKeyRole] = useState("Marketing");
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  async function handleCreate() {
    try {
      const result = await createKey.mutateAsync({
        name: newKeyName,
        role: newKeyRole,
      });
      setCreatedKey(result.apiKey);
      setNewKeyName("");
      toast.success("API key created");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create key");
    }
  }

  async function handleRotate(id: string) {
    try {
      const result = await rotateKey.mutateAsync(id);
      setCreatedKey(result.apiKey);
      toast.success("API key rotated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to rotate key");
    }
  }

  async function handleRevoke(id: string) {
    try {
      await revokeKey.mutateAsync(id);
      toast.success("API key revoked");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to revoke key");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Keys</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage programmatic access keys</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Key
        </Button>
      </div>

      {/* Show newly created key */}
      {createdKey && (
        <Card className="border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">New API Key (copy now - it won't be shown again)</p>
                <p className="mt-1 font-mono text-sm text-green-900 dark:text-green-200">{createdKey}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(createdKey);
                    toast.success("Copied");
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCreatedKey(null)}>
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Create API Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="My integration key"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Role Scope</label>
              <select
                className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                value={newKeyRole}
                onChange={(e) => setNewKeyRole(e.target.value)}
              >
                <option value="Admin">Admin</option>
                <option value="Marketing">Marketing</option>
                <option value="CustomerSuccess">Customer Success</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={!newKeyName || createKey.isPending}>
                {createKey.isPending ? "Creating..." : "Create"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keys Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400" />
            </div>
          ) : !keys?.length ? (
            <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">No API keys yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Prefix</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                    <th className="px-4 py-3 font-medium">Last Used</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key) => (
                    <tr key={key.id} className="border-b last:border-0">
                      <td className="px-4 py-3 font-medium">{key.name}</td>
                      <td className="px-4 py-3 font-mono text-gray-500 dark:text-gray-400">{key.prefix}...</td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary">{key.role}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={key.status === "Active" ? "success" : "destructive"}>
                          {key.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(key.createdAt)}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {key.lastUsedAt ? formatDate(key.lastUsedAt) : "Never"}
                      </td>
                      <td className="px-4 py-3">
                        {key.status === "Active" && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Rotate"
                              onClick={() => handleRotate(key.id)}
                            >
                              <RotateCw className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 dark:text-red-400"
                              title="Revoke"
                              onClick={() => handleRevoke(key.id)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
